import { Button, Flex, useToast } from "@chakra-ui/react";
import type { APIListingAgentUser } from "models/types/api-listing";
import { useAuth } from "contexts/auth-provider";

type MarketingFeedbackButtonsProps = {
  primaryContact: APIListingAgentUser;
  primaryContactEmail: string;
  secondaryContactEmail?: string;
  address?: string;
  recordId: number;
};

export function MarketingFeedbackButtons({
  primaryContact,
  primaryContactEmail,
  secondaryContactEmail,
  address,
  recordId,
}: MarketingFeedbackButtonsProps) {
  const { user } = useAuth();

  const toast = useToast();

  function generateMailToTemplate(type: "feedback" | "approval") {
    const urlParams = new URLSearchParams();

    const subject = `Re: ${address ?? ""} ${recordId} - ${
      type === "feedback"
        ? "Feedback on marketing content"
        : "Approve marketing content"
    }`;
    urlParams.append("subject", subject);

    if (secondaryContactEmail) {
      urlParams.append("cc", secondaryContactEmail);
    }

    const body =
      type === "feedback"
        ? `Hi ${primaryContact.name || "there"},\n\n` +
          `Thanks for sending through the marketing materials for ${
            address || "the property"
          }.\n\n` +
          `I've had a look and have a few changes I'd like to suggest:\n` +
          `[Insert your feedback here]\n\n` +
          `Please let me know once the updates have been made, or if you'd like to discuss anything further.\n\n` +
          `Kind regards,\n` +
          `${user?.given_name || "[Your name]"}`
        : `Hi ${primaryContact.name || "there"},\n\n` +
          `I’ve reviewed everything and I’m happy to confirm my approval.\n\n` +
          `Kind regards,\n` +
          `${user?.given_name || "[Your name]"}`;

    urlParams.append("body", body);

    return `mailto:${`${primaryContactEmail}?${urlParams
      .toString()
      .replace(/\+/g, "%20")}`}`;
  }

  function renderToast() {
    if (!toast.isActive("marketing-material-toast")) {
      toast({
        id: "marketing-material-toast",
        title: "Nothing happening?",
        description: `If nothing is happening, you may not have a default email client configured on your computer. That's okay, you can still email feedback to ${
          primaryContact.name ?? "your agent"
        } at ${primaryContactEmail}`,
        isClosable: true,
        duration: null,
        position: "top-right",
        variant: "subtle",
      });
    }
  }

  return (
    <Flex gap={4}>
      <Button
        as={"a"}
        onClick={renderToast}
        bgColor={"gray.100"}
        _hover={{ bg: "gray.300" }}
        textColor={"black"}
        cursor={"pointer"}
        href={generateMailToTemplate("feedback")}
      >
        Send feedback
      </Button>
      <Button
        onClick={renderToast}
        colorScheme={"gray"}
        as={"a"}
        cursor={"pointer"}
        href={generateMailToTemplate("approval")}
      >
        Approve
      </Button>
    </Flex>
  );
}
